import { useState, useRef, useEffect } from 'react';
import { X, Plus, ChevronDown } from 'lucide-react';
import type { LeadTag } from '../../types/crm';

interface TagSelectProps {
  tags: LeadTag[];
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  onCreateTag: (name: string) => Promise<LeadTag>;
}

export function TagSelect({ tags, selectedTagIds, onChange, onCreateTag }: TagSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedTags = tags.filter((t) => selectedTagIds.includes(t.id));
  const filteredTags = tags.filter(
    (t) =>
      !selectedTagIds.includes(t.id) &&
      t.name.toLowerCase().includes(search.toLowerCase())
  );
  const showCreateOption =
    search.trim() &&
    !tags.some((t) => t.name.toLowerCase() === search.toLowerCase());

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    onChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!search.trim() || isCreating) return;
    setIsCreating(true);
    try {
      const newTag = await onCreateTag(search.trim());
      onChange([...selectedTagIds, newTag.id]);
      setSearch('');
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && showCreateOption) {
      e.preventDefault();
      handleCreateTag();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="min-h-[42px] px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-xl cursor-pointer flex flex-wrap gap-2 items-center bg-white dark:bg-slate-700 hover:border-gray-300 dark:hover:border-slate-500 transition-colors"
      >
        {selectedTags.length === 0 && !isOpen && (
          <span className="text-gray-400 dark:text-slate-400 text-sm">Tags auswählen...</span>
        )}
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-lg text-sm"
          >
            {tag.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(tag.id);
              }}
              className="hover:text-primary-dark"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {isOpen && (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={selectedTags.length > 0 ? '' : 'Suchen oder erstellen...'}
            className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
          />
        )}
        {!isOpen && (
          <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg max-h-60 overflow-auto">
          {filteredTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleToggleTag(tag.id)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-600 dark:text-white flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-primary/50" />
              {tag.name}
            </button>
          ))}
          {showCreateOption && (
            <button
              type="button"
              onClick={handleCreateTag}
              disabled={isCreating}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2 text-primary border-t border-gray-100 dark:border-slate-600"
            >
              <Plus className="w-4 h-4" />
              "{search}" erstellen
            </button>
          )}
          {filteredTags.length === 0 && !showCreateOption && (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400 text-center">
              {search ? 'Keine Tags gefunden' : 'Keine weiteren Tags'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
